'use client';

import {
  getClientApplicationConfig,
  getClientApplicationContext,
} from '@hc-frontend/core-context';
import type { EventBusListener } from '@hc-frontend/core-events';
import { type CostModel } from '@hc-frontend/core-types';
import { useEventBus } from '@hc-frontend/core-ui';
import { pushAdClickData } from '@hc-frontend/services';
import {
  buildAdContextV2,
  trackAdClick as trackAdClickSnowplow,
  trackAdImpression as trackAdImpressionSnowplow,
} from '@hc-frontend/third-party-snowplow';
import { useCallback, useEffect } from 'react';

import type { CompositionRoot } from '../../../config/root/client';
import { useCompositionRoot } from '../../../config/root/client';
import type {
  AdClickEvent,
  AdEventTarget,
  AdImpressionEvent,
} from './ad-events-subscription.types';

function trackAdImpressionWithContext(
  {
    featuredAds,
    contexts = [],
    placement,
    campaignPlacement,
    adsSource,
    sessionId,
    productType,
    subProductType,
    device,
    stateAcronym,
    zipCode,
    domain,
  }: AdImpressionEvent,
  { trackAdImpression }: Pick<CompositionRoot, 'trackAdImpression'>,
): void {
  trackAdImpression({
    featureAds: {
      result: featuredAds.result,
      id: featuredAds.id,
    },
    productType,
    device,
    placement,
    sessionId,
    stateAcronym,
    zipCode,
    domain,
  });

  const clientContext = getClientApplicationContext(
    trackAdImpressionWithContext,
  );

  clientContext.trackCountMetric({
    name: 'ad-impression',
    tags: {
      placement,
      campaignPlacement,
      adsSource,
      sessionId,
      product: productType,
      stateAcronym,
      zipCode,
      domain,
    },
  });

  for (const [index, ad] of featuredAds.result.entries()) {
    const impressionPosition = index + 1;

    const adContextV2 = buildAdContextV2({
      adServer: ad.adsSource,
      advertiser: {
        creativeId: ad.creativeId,
        creativeTitle: ad.title,
        name: ad.provider.name,
        campaignName: ad.campaignName,
      },
      publisher: {
        adId: ad.id,
        productType,
        subProductType,
        placement,
      },
      impressionPosition,
      searchMethod: ad.searchMethod,
      productFit: ad.segmentationFit,
      groupId: ad.groupId,
    });

    trackAdImpressionSnowplow(
      {
        impressionId: featuredAds.id,
        impressionOrder: impressionPosition.toString(),
        bannerId: ad.creativeId.toString(),
        campaignId: ad.campaignId ? `${ad.campaignId}` : undefined,
        advertiserId: ad.provider.id ? `${ad.provider.id}` : undefined,
        targetUrl: ad.link,
        costModel: ad.dealType.toLowerCase() as 'cpc' | 'cpa',
        cost: ad.cpc,
      },
      [...contexts, adContextV2],
    );
  }
}

function trackAdClickWithContext(
  {
    productType,
    subProductType,
    featuredAd,
    impressionPosition,
    impressionId,
    device,
    placement,
    sessionId,
    contexts,
    stateAcronym,
    zipCode,
    clickCount,
    domainUserId,
    domainSessionId,
    userId,
    clickPlatform,
    clickId,
    clickTimestamp,
  }: AdClickEvent,
  {
    trackAdClick,
    trackAdConversion,
  }: Pick<CompositionRoot, 'trackAdClick' | 'trackAdConversion'>,
  source: string,
): void {
  const { link, dealType, cpc, provider, campaignId, id, adsSource } =
    featuredAd;
  const config = getClientApplicationConfig();

  const adContextV2 = buildAdContextV2({
    adServer: featuredAd.adsSource,
    advertiser: {
      creativeId: featuredAd.creativeId,
      creativeTitle: featuredAd.title,
      name: featuredAd.provider.name,
      campaignName: featuredAd.campaignName,
    },
    publisher: {
      adId: featuredAd.id,
      productType,
      subProductType,
      placement,
    },
    impressionPosition,
    searchMethod: featuredAd.searchMethod,
    productFit: featuredAd.segmentationFit,
    groupId: featuredAd.groupId,
  });
  const contextsWithAdContextV2 = contexts
    ? [...contexts, adContextV2]
    : [adContextV2];

  trackAdClickSnowplow(
    {
      targetUrl: link,
      costModel: dealType.toLowerCase() as Lowercase<CostModel>,
      cost: cpc,
      advertiserId: provider.id ? `${provider.id}` : undefined,
      campaignId: campaignId ? `${campaignId}` : undefined,
      impressionId,
    },
    contextsWithAdContextV2,
  );

  trackAdClick({
    featureAd: {
      id,
      provider,
      link,
      dealType,
      cpc,
    },
    clickCount,
    impressionId,
    device,
    placement,
    productType,
    sessionId,
    searchAdsId: impressionId,
    blockPosition: impressionPosition,
    pagePosition: impressionPosition,
    stateAcronym,
    zipCode,
  });

  // Push the ad click data to the ETL pipeline
  if (config.core.appEnvironment === 'prd' && adsSource === 'AdButler') {
    pushAdClickData(
      config.etl.url,
      {
        id,
        advertiserId: provider.id,
        advertiserName: provider.name,
        displayUrl: provider.displayUrl,
        link,
        dealType,
        cpc,
        adsSource,
        clickCount,
        impressionId,
        device,
        placement,
        productType,
        sessionId,
        blockPosition: impressionPosition,
        pagePosition: impressionPosition,
        stateAcronym,
        zipCode,
        domainUserId,
        domainSessionId,
        userId,
        clickPlatform,
        clickId,
        clickTimestamp,
      },
      {
        headers: {
          Authorization: `Bearer ${config.etl.authToken}`,
        },
      },
    );
  }

  trackAdConversion({
    placement,
    sessionId,
    featureAd: {
      cpc,
      id,
    },
    featureAds: {
      id: impressionId,
    },
  });

  // NOSONAR
  window.dataLayer.push(
    { cpc },
    { event: `fa_conversion_${dealType.toLowerCase()}_click` },
  );

  // Bing - Tracking - TYP
  if (['30', '33', '300', '330', '300D', '330D'].includes(source)) {
    // NOSONAR
    window.uetq.push({
      ec: 'Conversion',
      ea: 'Click',
      el: placement,
      ev: cpc,
      gv: cpc,
    });
  }
}

export function AdEventsSubscription({ source }: { source: string }) {
  const eventBus = useEventBus();
  const { trackAdConversion, trackAdClick, trackAdImpression } =
    useCompositionRoot();

  const handleAdImpression = useCallback<
    EventBusListener<AdImpressionEvent, AdEventTarget>
  >(
    (event) => trackAdImpressionWithContext(event, { trackAdImpression }),
    [trackAdImpression],
  );

  const handleAdClick = useCallback<
    EventBusListener<AdClickEvent, AdEventTarget>
  >(
    (event) =>
      trackAdClickWithContext(
        event,
        { trackAdConversion, trackAdClick },
        source,
      ),
    [trackAdClick, trackAdConversion, source],
  );

  useEffect(() => {
    eventBus.subscribe('core.ui.ad.impression', handleAdImpression);
    eventBus.subscribe('core.ui.ad.click', handleAdClick);

    return () => {
      eventBus.unsubscribe('core.ui.ad.impression', handleAdImpression);
      eventBus.unsubscribe('core.ui.ad.click', handleAdClick);
    };
  }, [handleAdClick, handleAdImpression, eventBus]);

  return null;
}
